// Single source of truth for keeping Angels (children who have passed away)
// out of every working "family members" list in the app, while still counting
// them as part of the family.
//
// Why this exists (recurring production issue): Angels are synced from the CRM
// as FamilyMember rows labelled relationship='Angel'. If any screen renders the
// raw `user.familyMembers` it will show the deceased child in a functional list
// — distressing for bereaved families. Rather than remembering to filter on
// every screen (which kept regressing), we partition ONCE at the AuthContext
// chokepoint: `user.familyMembers` becomes living-only (the default everything
// reads), and angels are kept separately in `user.angelMembers` for the family
// size count only. A new screen that naively maps `user.familyMembers` is then
// safe by construction — an angel can never leak into a list or a selector.

import type { FamilyMember, User } from '../types';
import { isAngelMember } from './attendee';

/**
 * Splits a family-member roster into living members and angels.
 * Angel detection is case-insensitive (relationship 'Angel'/'angel').
 */
export function partitionFamilyMembers(
  members: FamilyMember[] | undefined | null,
): { living: FamilyMember[]; angels: FamilyMember[] } {
  if (!Array.isArray(members)) return { living: [], angels: [] };
  const living: FamilyMember[] = [];
  const angels: FamilyMember[] = [];
  for (const m of members) {
    (isAngelMember(m) ? angels : living).push(m);
  }
  return { living, angels };
}

/**
 * Normalises a user so that `familyMembers` is living-only and angels are
 * segregated into `angelMembers`. Applied at the AuthContext chokepoint so
 * every consumer of `user.familyMembers` is angel-free by default. Null-safe.
 */
export function sanitizeUser(user: User): User {
  if (!user) return user;
  const { living, angels } = partitionFamilyMembers(user.familyMembers);
  const result: User = { ...user, familyMembers: living, angelMembers: angels };
  // Deprecated `children` field: keep it angel-free too so any legacy consumer
  // (e.g. ProfilePage's children fallback) can never render a deceased child.
  if (Array.isArray(user.children)) {
    const childPart = partitionFamilyMembers(user.children);
    result.children = childPart.living;
    // If familyMembers wasn't the source, surface the children-derived angels
    // so they still count towards family size.
    if (living.length === 0 && angels.length === 0 && childPart.angels.length > 0) {
      result.angelMembers = childPart.angels;
    }
  }
  return result;
}

/**
 * Total family size INCLUDING angels — a child who has passed away is still
 * part of the family. Adds +1 for the logged-in primary contact when they are
 * not already present in the roster (mirrors the historical rule).
 */
export function computeFamilySize(
  user: Pick<User, 'firstName' | 'lastName' | 'familyMembers' | 'angelMembers'> | null | undefined,
): number {
  const living = user?.familyMembers ?? [];
  const angels = user?.angelMembers ?? [];
  const total = living.length + angels.length;
  const userName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.toLowerCase().trim();
  const primaryInRoster =
    !!userName &&
    [...living, ...angels].some((m) => m.name.toLowerCase().trim() === userName);
  return primaryInRoster ? total : total + 1;
}
